import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { Home, Tv, Radio, Users, Settings as SettingsIcon, LogOut, Film, FolderPlus, Menu, X, UserCheck } from 'lucide-react';
import * as api from './services/api';

// Admin Pages
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Channels from './pages/Channels';
import Movies from './pages/Movies';
import Series from './pages/Series';
import CustomCategories from './pages/CustomCategories';
import EPG from './pages/EPG';
import UsersPage from './pages/Users';
import Settings from './pages/Settings';
import Customers from './pages/admin/Customers';
import ServerStatus from './pages/admin/ServerStatus';

// Customer Pages
import CustomerLogin from './pages/customer/Login';
import CustomerDashboard from './pages/customer/Dashboard';
import CustomerSettings from './pages/customer/Settings';
import Duplicates from './pages/customer/Duplicates';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
      setIsAuthenticated(true);
    }
    setLoading(false);
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    const wasCustomer = user?.role === 'customer';
    try {
      if (wasCustomer) { await api.customerLogout(); }
      else { await api.logout(); }
    } catch (error) {}
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setIsAuthenticated(false);
    window.location.href = wasCustomer ? '/customer/login' : '/login';
  };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <Router>
      <Routes>
        <Route path="/login" element={isAuthenticated ? (user?.role === 'customer' ? <Navigate to="/dashboard" replace /> : <Navigate to="/" replace />) : <Login onLogin={handleLogin} />} />
        <Route path="/customer/login" element={isAuthenticated && user?.role === 'customer' ? <Navigate to="/dashboard" replace /> : <CustomerLogin onLogin={handleLogin} />} />

        {/* Single Layout for all authenticated users — sidebar adapts to role */}
        <Route path="/*" element={isAuthenticated ? <Layout user={user} onLogout={handleLogout}><Routes>
          <Route path="/" element={user?.role === 'customer' ? <CustomerDashboard /> : <Dashboard />} />
          <Route path="/" element={user?.role === 'customer' ? <CustomerDashboard /> : <Dashboard />} />
          <Route path="/dashboard" element={user?.role === 'customer' ? <CustomerDashboard /> : <Navigate to="/" replace />} />
          <Route path="/channels" element={<Channels />} />
          <Route path="/movies" element={<Movies />} />
          <Route path="/series" element={<Series />} />
          <Route path="/custom-categories" element={<CustomCategories />} />
          <Route path="/epg" element={user?.role === 'customer' ? <Navigate to="/" replace /> : <EPG />} />
          <Route path="/users" element={user?.role === 'customer' ? <Navigate to="/" replace /> : <UsersPage />} />
          <Route path="/customers" element={user?.role === 'customer' ? <Navigate to="/" replace /> : <Customers />} />
          <Route path="/server-status" element={user?.role === 'customer' ? <Navigate to="/" replace /> : <ServerStatus />} />
          <Route path="/duplicates" element={user?.role === 'customer' ? <Duplicates /> : <Navigate to="/" replace />} />
          <Route path="/settings" element={user?.role === 'customer' ? <CustomerSettings /> : <Settings />} />
        </Routes></Layout> : <Navigate to={sessionStorage.getItem('loginContext') === 'customer' ? '/customer/login' : '/login'} replace />} />
      </Routes>
    </Router>
  );
}

function Layout({ user, onLogout, children }) {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar on route change (mobile)
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  const isCustomer = user?.role === 'customer';
  const navItems = isCustomer ? [
    { path: '/dashboard', label: 'Dashboard', icon: Home },
    { path: '/channels', label: 'Channels', icon: Radio },
    { path: '/movies', label: 'Movies', icon: Film },
    { path: '/series', label: 'Series', icon: Tv },
    { path: '/custom-categories', label: 'Custom Categories', icon: FolderPlus },
    { path: '/duplicates', label: 'Duplicates', icon: FolderPlus },
    { path: '/settings', label: 'Settings', icon: SettingsIcon }
  ] : [
    { path: '/', label: 'Dashboard', icon: Home },
    { path: '/channels', label: 'Channels', icon: Radio },
    { path: '/movies', label: 'Movies', icon: Film },
    { path: '/series', label: 'Series', icon: Tv },
    { path: '/custom-categories', label: 'Custom Categories', icon: FolderPlus },
    { path: '/epg', label: 'EPG', icon: SettingsIcon },
    { path: '/customers', label: 'Customers', icon: UserCheck },
    { path: '/server-status', label: 'Server Status', icon: Tv },
    { path: '/users', label: 'Legacy Users', icon: Users },
    { path: '/settings', label: 'Settings', icon: SettingsIcon }
  ];

  return (
    <div className="app">
      {/* Mobile overlay */}
      {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />}

      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <img src="/logo.png" alt="StreamUnity" className="sidebar-logo" />
          <button className="sidebar-close-btn" onClick={() => setSidebarOpen(false)}>
            <X size={20} />
          </button>
        </div>
        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>
      </aside>

      <main className="main-content">
        <div className="top-nav">
          <button className="hamburger-btn" onClick={() => setSidebarOpen(true)}>
            <Menu size={24} />
          </button>
          <div className="top-nav-title">{isCustomer ? 'My IPTV Service' : 'StreamUnity'}</div>
          <div className="user-menu">
            <span className="user-info">👤 <strong>{user?.username}</strong></span>
            <button className="logout-btn" onClick={onLogout}>
              <LogOut size={16} />
              <span className="hide-mobile">Logout</span>
            </button>
          </div>
        </div>
        <div className="page-content">
          {children}
        </div>
      </main>
    </div>
  );
}

export default App;
